import {
	ProjectActivityMaterialEditApi,
	ProjectActivityMaterialEditApi_FormInitData,
} from "src/api/generated/erp/project/projectActivityMaterial/api/projectActivityMaterialEditApi.ts";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { ProjectActivityMaterial } from "src/api/generated/erp/db/types/tables/projectActivityMaterial.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { concatWithPipe } from "src/utils/strings.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { Typography } from "@mui/material";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { useState } from "react";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { SupplierQueryApi } from "src/api/generated/erp/purchase/suppliers/api/supplierQueryApi.ts";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { SupplierPartView } from "src/api/generated/erp/db/types/tables/supplierPartView.ts";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProjectActivityMaterialLineFormProps extends FormCommonProps<number> {
	activityId: number;
	materialLineId: number | undefined;
}

interface FormValues extends ProjectActivityMaterial {
	part: PartView;
}

export const ProjectActivityMaterialForm = (props: ProjectActivityMaterialLineFormProps) => {
	const { activityId, materialLineId, onCompleted, onFormEdited } = props;
	return (
		<AsyncForm<ProjectActivityMaterialEditApi_FormInitData, FormValues, number>
			fetch={() =>
				ProjectActivityMaterialEditApi.getFormInitData({
					activityId,
					materialLineId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={2}
			agentContext={{
				key: defineAgentUiContextKey("erp.project.projectActivityMaterial.form"),
				data: {
					activityId: activityId,
					materialLineId: materialLineId ?? null,
				},
			}}
			render={(renderParams) => <FormContent {...renderParams} {...props} />}
		/>
	);

	function getDefaultValues({
		activity,
		materialLine,
		part,
		defaultLineNumber,
	}: ProjectActivityMaterialEditApi_FormInitData): Partial<FormValues> {
		if (materialLine != null)
			return {
				...materialLine,
				part: part ?? undefined,
			};
		else
			return {
				activityId: activityId,
				lineNumber: defaultLineNumber,
				demandDate: activity.plannedStartDate,
			};
	}

	async function submit(values: FormValues) {
		const materialLine = {
			...values,
			part: undefined,
			partId: values.part?.partId,
		};
		if (materialLineId == null) {
			return await ProjectActivityMaterialEditApi.insert({ materialLine });
		} else {
			await ProjectActivityMaterialEditApi.update({ request: materialLine });
			return materialLineId;
		}
	}
};

interface FormContentProps
	extends
		ProjectActivityMaterialLineFormProps,
		AsyncFormContentParams<ProjectActivityMaterialEditApi_FormInitData, FormValues> {}

const FormContent = ({
	control,
	watch,
	setValue,
	activityId,
	data: { materialLine, supplierPartOptions: initialSupplierPartOptions },
}: FormContentProps) => {
	const isExistingRecord = materialLine != null;
	const materialIsAlreadyReleased = materialLine != null && materialLine.state === "RELEASED";

	const [supplierPartOptions, setSupplierPartOptions] = useState<SupplierPartView[]>(
		initialSupplierPartOptions ?? [],
	);

	const part = watch("part");
	const acquisitionMethod = watch("acquisitionMethod");

	return (
		<>
			<FormAsyncSelectField
				control={control}
				name={"part"}
				formValueType={"option"}
				label={i18n.t("part")}
				getOptionKey={(o: PartView) => o.partId}
				getOptionLabel={(o) => concatWithPipe(o.partNo, o.partDescription_1, o.partDescription_2)}
				fetchOptions={({ currentSelection, searchQuery }) =>
					ProjectActivityMaterialEditApi.getPartOptions({
						currentSelection,
						activityId,
						searchQuery,
					})
				}
				rules={requireRule()}
				disabled={isExistingRecord}
				onChange={async (newPart) => {
					await onPartChanged(newPart);
				}}
			/>
			{part && (
				<Typography
					children={`${i18n.t("base_unit")}: ${part.partUnit}`}
					sx={{
						alignSelf: "center",
					}}
				/>
			)}
			<FormEnumSelectField
				control={control}
				name={"acquisitionMethod"}
				label={i18n.t("acquisition_method")}
				options={getAcquisitionMethodLabels()}
				rules={requireRule()}
				disabled={materialIsAlreadyReleased}
				startNewGridRow
			/>
			<FormNumberField
				control={control}
				name={"quantityInBaseUnit"}
				label={i18n.t("quantity")}
				rules={requireRule()}
				disabled={materialIsAlreadyReleased}
			/>
			<FormNumberField control={control} name={"lineNumber"} label={i18n.t("line_no")} type={"integer"} />
			<FormTextField control={control} name={"mark"} label={i18n.t("mark")} />
			<FormDateField control={control} name={"demandDate"} label={i18n.t("demand_date")} rules={requireRule()} />
			{["PURCHASE_TRANSIT", "PURCHASE_DIRECT"].includes(acquisitionMethod) && (
				<FormSelectField
					control={control}
					name={"supplierId"}
					label={i18n.t("supplier")}
					options={supplierPartOptions}
					getOptionKey={(o) => o.supplierId}
					getOptionLabel={(o) => o.supplierName}
					rules={requireRule()}
					disabled={materialIsAlreadyReleased}
					disableClearable
				/>
			)}
			{acquisitionMethod === "MANUFACTURE" && (
				<FormTextField
					control={control}
					name={"shopOrderBatchCode"}
					label={i18n.t("shop_order_batch")}
					disabled={materialIsAlreadyReleased}
				/>
			)}
		</>
	);

	async function onPartChanged(part: PartView | null) {
		const defaultAcquisitionMethod = part?.acquisitionMethod;
		setValue("acquisitionMethod", defaultAcquisitionMethod);

		if (
			defaultAcquisitionMethod != null &&
			["PURCHASE_TRANSIT", "PURCHASE_DIRECT"].includes(defaultAcquisitionMethod)
		) {
			const newPartSupplierParts =
				part == null ?
					[]
				:	await SupplierQueryApi.getPartSupplierOptions({
						partId: part.partId,
					});
			setSupplierPartOptions(newPartSupplierParts);
			setValue("supplierId", newPartSupplierParts[0]?.supplierId);
		} else {
			setSupplierPartOptions([]);
			setValue("supplierId", null);
		}
	}
};
